import React from 'react';
import { ProgressBreadcrumbCompletedDisplayStyle, ProgressBreadcrumbListTheme } from './ProgressBreadcrumbList';
import { faCheck, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProgressBar, ProgressBarInfo } from '../ProgressBar/ProgressBar';

export const ProgressBreadcrumb: React.FC<{
    id: string;
    number: number;
    children: any;
    currentNumber?: number; //should be handled through the ProgressBreadcrumbList and passed through
    hasProgressed?: boolean; //should be handled through the ProgressBreadcrumbList activeCrumbNumber and passed through
    handleClick?: React.MouseEventHandler;
    enabled?: boolean;
    disabled?: boolean; //greyed out and not clickable
    theme?: ProgressBreadcrumbListTheme;
    completedCrumbDisplayStyle?: ProgressBreadcrumbCompletedDisplayStyle;
    progressBarInfo?: ProgressBarInfo; //optional; rendered beneath this crumb only while it is the current crumb
    hasError?: boolean; //optional; shows a red bubble with a warning icon in place of its number or checkmark
    errorHint?: string; //optional; shown on hovering the bubble while hasError (e.g. what's still missing)
}> = ({
    id,
    number,
    children,
    currentNumber = 0,
    hasProgressed = false,
    handleClick,
    enabled = true,
    disabled = false,
    theme = ProgressBreadcrumbListTheme.Blue,
    completedCrumbDisplayStyle,
    progressBarInfo,
    hasError = false,
    errorHint,
}) => {
    const isClickable = enabled && !disabled;

    const setStyle = {
        cursor: `${disabled ? 'not-allowed' : isClickable ? 'pointer' : 'arrow'}`,
    };

    const performClick = (e: React.MouseEvent) => {
        if (isClickable && handleClick) {
            handleClick(e);
        }
    };

    const status = () => {
        if (number == currentNumber) {
            return 'current';
        } else if (hasProgressed) {
            return 'completed';
        } else {
            return;
        }
    };

    const renderNode = () => {
        if (hasError) {
            return <FontAwesomeIcon icon={faWarning} />;
        } else if (hasProgressed && number != currentNumber) {
            switch (completedCrumbDisplayStyle) {
                case ProgressBreadcrumbCompletedDisplayStyle.Checkmark:
                    return <FontAwesomeIcon icon={faCheck} />;
                case ProgressBreadcrumbCompletedDisplayStyle.Number:
                    return number;
                default:
                    return number;
            }
        } else {
            return number;
        }
    };

    return (
        <>
            <div
                id={id}
                className={`breadcrumb-item ${status()}${disabled ? ' disabled' : ''}`}
                key={`breadcrumb_${id}_${number}`}
                onClick={performClick}
                style={setStyle}
            >
                <div className='breadcrumb-content'>
                    <div
                        className={`breadcrumb-bubble ${status()} ${theme}${hasError ? ' error' : ''}`}
                        title={hasError ? errorHint : undefined}
                    >
                        {renderNode()}
                    </div>
                    <div className={`text-content ${status()}`}>{children}</div>
                </div>
                {progressBarInfo && number == currentNumber && (
                    <div className='breadcrumb-progress'>
                        <ProgressBar
                            id={`${id}_progress`}
                            progress={progressBarInfo}
                        />
                    </div>
                )}
                {hasProgressed && number < currentNumber && <div className={`line ${theme}`}></div>}
            </div>
        </>
    );
};
